// The event's scope table (Lane 1322): server-paged lines, filters, bulk
// include/exclude/remove, add parts by filters, paste or upload a part list,
// and per-line volume, buying step and incumbent. Every change goes through
// the server's action layer and the table reloads from saved data.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableShell, Pager } from '../../components/ui/DataPrimitives';
import ScopeBuilder, { SupplierPicker, emptyFilters, toServerFilters } from './ScopeBuilder';
import {
  INCOMPLETE_TEXT, IMPORT_REASON_TEXT, formatDate, formatMoney, formatPrice, formatUnits,
  parsePastedParts, STALE_MESSAGE,
} from './sourcingApi';

const PAGE_SIZES = [50, 100, 250, 500];

function QuantityCell({ line, editable, onSave }) {
  const [value, setValue] = useState(line.quantity ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setValue(line.quantity ?? ''); }, [line.quantity]);
  const commit = () => {
    setEditing(false);
    const next = String(value).trim() === '' ? null : Number(String(value).replace(/,/g, ''));
    if (next !== null && (!Number.isFinite(next) || next <= 0)) { setValue(line.quantity ?? ''); return; }
    if (next === line.quantity) return;
    onSave(next);
  };
  const source = line.quantity_source === 'buyer'
    ? `changed by ${line.quantity_set_by_name || 'a buyer'}`
    : 'from last 12 months';
  if (editable && editing) {
    return (
      <input className="srce-input srce-input--num" autoFocus value={value} aria-label="Estimated annual volume"
        onChange={(e) => setValue(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setValue(line.quantity ?? ''); setEditing(false); } }} />
    );
  }
  return (
    <button type="button" className="srce-qty" disabled={!editable} onClick={() => setEditing(true)}
      title={editable ? 'Change the estimated annual volume' : undefined}>
      <span className="srce-num">{line.quantity === null ? 'Enter volume' : formatUnits(line.quantity)}</span>
      <span className="srce-sub">{line.quantity === null ? '' : source}</span>
    </button>
  );
}

function LineDetail({ line, editable, api, onAct }) {
  const [step, setStep] = useState(line.step);
  const [picking, setPicking] = useState(false);
  return (
    <div className="srce-detail">
      <div className="srce-detail-grid">
        <div><span className="srce-k">Units bought, 12 months</span>{formatUnits(line.history_units)} (returns subtracted)</div>
        <div><span className="srce-k">Spend, same window</span>{formatMoney(line.history_spend, 2)}</div>
        <div><span className="srce-k">Last paid at this site</span>
          {line.baseline_price === null ? 'No price at this site yet'
            : `${formatPrice(line.baseline_price)} on ${formatDate(line.baseline_date)} from ${line.baseline_supplier_name}`}</div>
        <div><span className="srce-k">Bought mostly from</span>
          {line.incumbent_supplier_name || 'No purchases in the window'}
          {line.incumbent_source === 'buyer' && line.incumbent_record_name && ` (buyer chose ${line.incumbent_record_name})`}</div>
        {line.approved_primary_supplier_name && line.approved_primary_supplier_name !== line.incumbent_supplier_name && (
          <div><span className="srce-k">Approved primary</span>{line.approved_primary_supplier_name}</div>
        )}
        {!line.is_included && <div><span className="srce-k">Left out because</span>{line.exclude_reason}</div>}
        <div><span className="srce-k">Figures as of</span>{formatDate(line.figures_as_of)}</div>
      </div>
      {editable && (
        <div className="srce-detail-actions">
          <label className="srce-inline">
            Buy in steps of
            <input className="srce-input srce-input--num" value={step} aria-label="Buying step"
              onChange={(e) => setStep(e.target.value.replace(/[^0-9]/g, ''))} />
          </label>
          <button type="button" className="srce-btn" disabled={!step || Number(step) === line.step || Number(step) < 1}
            onClick={() => onAct('POST', '/lines/step', { items: [{ line_uid: line.line_uid, step: Number(step) }] })}>
            Save step
          </button>
          {!picking && (
            <button type="button" className="srce-btn" onClick={() => setPicking(true)}>
              {line.incumbent_state === 'unresolved' ? 'Pick the current supplier' : 'Change current supplier'}
            </button>
          )}
          {picking && (
            <SupplierPicker api={api} value={null} placeholder="Current supplier"
              onChange={(s) => { setPicking(false); if (s) onAct('POST', '/lines/incumbent', { selection: { line_uids: [line.line_uid] }, supplier_id: s.supplier_id }); }} />
          )}
          {line.incumbent_source === 'buyer' && (
            <button type="button" className="srce-btn srce-btn--quiet"
              onClick={() => onAct('POST', '/lines/incumbent', { selection: { line_uids: [line.line_uid] }, supplier_id: null })}>
              Use purchase history again
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ScopeTab({ api, eventId, summary, onChanged, options }) {
  // After publish the table edits the change in progress (slice 2E), if any;
  // buyer-only fields (step, current supplier) stay draft-only.
  const amending = summary.status !== 'draft' && Boolean(summary.amendment);
  const editable = summary.status === 'draft' || amending;
  const [filter, setFilter] = useState({ search: '', level_1: '', site: '', view: 'included' });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sort, setSort] = useState('spend');
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(null);
  // Load errors are kept apart from action errors, so the reload that follows
  // a refused (stale) change cannot wipe the message about it.
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [mode, setMode] = useState(null); // 'add' | 'paste' | 'exclude'
  const [addFilters, setAddFilters] = useState(emptyFilters());
  const [pasteText, setPasteText] = useState('');
  const [reason, setReason] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const tableRef = useRef(null);
  const fileRef = useRef(null);

  const params = useCallback(() => {
    const p = { page, page_size: pageSize, sort, search: filter.search, level_1: filter.level_1, site: filter.site,
      version: amending ? 'amendment' : undefined };
    if (filter.view === 'included') p.included = true;
    if (filter.view === 'excluded') p.included = false;
    if (filter.view === 'incomplete') { p.included = true; p.incomplete = true; }
    return p;
  }, [page, pageSize, sort, filter, amending]);

  // Only the newest request may fill the table: an older, slower response
  // must never replace it, or a bulk action could act on lines not shown.
  const requestSeq = useRef(0);
  const [loadingLines, setLoadingLines] = useState(true);
  // The request key of the rows on screen; bulk actions show only when it
  // matches the current filters, so they never act on lines not shown.
  const [shownKey, setShownKey] = useState(null);
  const currentKey = JSON.stringify(params());
  const load = useCallback(() => {
    const seq = ++requestSeq.current;
    const p = params();
    setLoadingLines(true);
    setShownKey(null);   // nothing on screen counts as current until this answers
    api.lines(eventId, p)
      .then((r) => {
        if (seq === requestSeq.current) { setData(r); setShownKey(JSON.stringify(p)); setLoadError(''); }
      })
      .catch((e) => { if (seq === requestSeq.current) setLoadError(e.message); })
      .finally(() => { if (seq === requestSeq.current) setLoadingLines(false); });
  }, [api, eventId, params]);

  useEffect(() => {
    requestSeq.current += 1;   // a filter change retires any request in flight
    setLoadingLines(true);
    setShownKey(null);
    const t = setTimeout(load, filter.search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, filter.search]);
  // A change made elsewhere (chat, another tab) moves the event's revision:
  // reload the rows so the table never shows lines from before that change.
  const loadedRevision = data ? data.revision : null;
  useEffect(() => {
    if (loadedRevision !== null && loadedRevision !== summary.revision) load();
  }, [summary.revision, loadedRevision, load]);
  const current = !loadingLines && shownKey === currentKey;
  // Row edits are allowed only on rows that match the current filters.
  const rowEditable = editable && current;
  useEffect(() => { setSelected(new Set()); setConfirmRemove(false); }, [filter, page, pageSize]);
  useEffect(() => { setPage(1); }, [filter, pageSize]);

  const filterForServer = () => {
    const f = {};
    if (filter.search) f.search = filter.search;
    if (filter.level_1) f.level_1 = filter.level_1;
    if (filter.site) f.site = filter.site;
    if (filter.view === 'included') f.included = true;
    if (filter.view === 'excluded') f.included = false;
    if (filter.view === 'incomplete') { f.included = true; f.incomplete = true; }
    return f;
  };
  const selection = () => (selected.size > 0
    ? { line_uids: Array.from(selected) } : { filter: filterForServer() });
  const selectionText = selected.size > 0
    ? `${selected.size} selected ${selected.size === 1 ? 'line' : 'lines'}`
    : (data && data.total === 1 ? 'the 1 line shown by these filters'
      : `all ${data ? data.total.toLocaleString() : ''} lines shown by these filters`);

  const act = async (method, path, input) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.act(eventId, method, path, input, summary.revision);
      await onChanged();
      load();
      return r;
    } catch (e) {
      if (e.code === 'stale_revision') {
        setError(STALE_MESSAGE);
        await onChanged();
        load();
      } else {
        setError(e.message);
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const addByFilters = async () => {
    const r = await act('POST', '/lines', { filters: toServerFilters(addFilters) });
    if (r) {
      setNotice(`Added ${r.result.added.toLocaleString()} lines${r.result.already_in_scope ? ` (${r.result.already_in_scope} were already in the event)` : ''}.`);
      setMode(null);
      setAddFilters(emptyFilters());
    }
  };

  const showImport = (r) => {
    if (!r) return;
    setReport(r.result);
    setNotice(`Added ${r.result.added.toLocaleString()} of ${r.result.rows.toLocaleString()} rows.`);
    setMode(null);
    setPasteText('');
  };
  const importPaste = async () => {
    const rows = parsePastedParts(pasteText);
    if (!rows.length) { setError('Paste at least one part number.'); return; }
    showImport(await act('POST', '/lines/import', { rows }));
  };
  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.importFile(eventId, file, summary.revision);
      await onChanged();
      load();
      showImport(r);
    } catch (e) {
      if (e.code === 'stale_revision') { await onChanged(); load(); }
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const bulk = async (kind) => {
    if (kind === 'exclude') {
      const r = await act('POST', '/lines/exclude', { selection: selection(), reason: reason.trim() });
      if (r) { setNotice(`Left out ${r.result.excluded.toLocaleString()} lines.`); setMode(null); setReason(''); }
    } else if (kind === 'include') {
      const r = await act('POST', '/lines/include', { selection: selection() });
      if (r) setNotice(`Put back ${r.result.included.toLocaleString()} lines.`);
    } else if (kind === 'remove') {
      const r = await act('POST', '/lines/remove', { selection: selection() });
      if (r) setNotice(`Removed ${r.result.removed.toLocaleString()} lines from the event.`);
    }
  };

  const items = (data && data.items) || [];
  const allOnPage = items.length > 0 && items.every((l) => selected.has(l.line_uid));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(items.map((l) => l.line_uid)));
  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };
  const previewAdd = useCallback((f) => api.previewLines(eventId, f), [api, eventId]);

  return (
    <div>
      <div className="ll-fbar">
        <div className="ll-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input value={filter.search} placeholder="Search part number or name" aria-label="Search lines"
            onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
        </div>
        <select className="ll-sel" value={filter.view} aria-label="Which lines"
          onChange={(e) => setFilter({ ...filter, view: e.target.value })}>
          <option value="included">In the event</option>
          <option value="incomplete">Needs attention</option>
          <option value="excluded">Left out</option>
          <option value="all">All lines</option>
        </select>
        <select className="ll-sel" value={filter.site} aria-label="Site"
          onChange={(e) => setFilter({ ...filter, site: e.target.value })}>
          <option value="">All sites</option>
          {((options && options.sites) || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="ll-sel" value={filter.level_1} aria-label="Category"
          onChange={(e) => setFilter({ ...filter, level_1: e.target.value })}>
          <option value="">All categories</option>
          {((options && options.categories) || []).map((c) => <option key={c.level_1} value={c.level_1}>{c.label || c.level_1}</option>)}
        </select>
        <select className="ll-sel" value={sort} aria-label="Sort" onChange={(e) => setSort(e.target.value)}>
          <option value="spend">Sort: spend</option>
          <option value="projected">Sort: projected cost</option>
          <option value="volume">Sort: volume</option>
          <option value="material">Sort: part number</option>
          <option value="site">Sort: site</option>
        </select>
        {editable && (
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn" onClick={() => setMode(mode === 'add' ? null : 'add')}>Add parts</button>
            <button type="button" className="srce-btn" onClick={() => setMode(mode === 'paste' ? null : 'paste')}>Paste a list</button>
            <button type="button" className="srce-btn" onClick={() => fileRef.current && fileRef.current.click()}>Upload a file</button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.txt" hidden onChange={(e) => importFile(e.target.files[0])} />
          </span>
        )}
      </div>

      {mode === 'add' && (
        <section className="srce-panel">
          <ScopeBuilder api={api} options={options} filters={addFilters} onChange={setAddFilters} preview={previewAdd} />
          <div className="srce-actions">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={addByFilters}>Add these parts</button>
          </div>
        </section>
      )}
      {mode === 'paste' && (
        <section className="srce-panel">
          <label className="srce-label" htmlFor="srce-paste">One part per line. Add a site and a volume after a comma or a tab if you have them.</label>
          <textarea id="srce-paste" className="srce-textarea" rows={8} value={pasteText}
            placeholder={'NG-4JBACE6\nNG-ZTL2DRD, Lancaster Plant, 5000'} onChange={(e) => setPasteText(e.target.value)} />
          <div className="srce-actions">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || !pasteText.trim()} onClick={importPaste}>Add these parts</button>
          </div>
        </section>
      )}

      {notice && <div className="srce-notice" role="status">{notice}<button type="button" className="ll-clr" onClick={() => setNotice('')}>Dismiss</button></div>}
      {error && <div className="srce-error" role="alert">{error}</div>}
      {loadError && <div className="srce-error" role="alert">{loadError}</div>}
      {report && (report.unmatched_count > 0 || report.ambiguous_count > 0) && (
        <section className="srce-panel srce-report">
          <div className="srce-strong">Rows that were not added</div>
          <ul>
            {report.unmatched.map((u) => (
              <li key={`u${u.row}`}>Row {u.row}: {u.material_number}{u.site ? ` at ${u.site}` : ''}. {IMPORT_REASON_TEXT[u.reason] || u.reason}.</li>
            ))}
            {report.ambiguous.map((a) => (
              <li key={`a${a.row}`}>Row {a.row}: {a.material_number} is bought at {a.sites.join(', ')}. Add the site you mean.</li>
            ))}
          </ul>
          {report.report_truncated && <div className="srce-mut">Only the first rows of each kind are listed ({report.unmatched_count} not found, {report.ambiguous_count} need a site).</div>}
          <button type="button" className="ll-clr" onClick={() => setReport(null)}>Dismiss</button>
        </section>
      )}

      {editable && data && data.total > 0 && current && (
        <div className="srce-bulk">
          <span className="srce-mut">Acting on {selectionText}</span>
          {filter.view !== 'excluded' && <button type="button" className="srce-btn" disabled={busy} onClick={() => setMode(mode === 'exclude' ? null : 'exclude')}>Leave out</button>}
          {filter.view !== 'included' && <button type="button" className="srce-btn" disabled={busy} onClick={() => bulk('include')}>Put back</button>}
          {!confirmRemove && <button type="button" className="srce-btn srce-btn--quiet" disabled={busy} onClick={() => setConfirmRemove(true)}>Remove from event</button>}
          {confirmRemove && (
            <span className="srce-row">
              <span className="srce-crit">Remove {selectionText}?</span>
              <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={() => { setConfirmRemove(false); bulk('remove'); }}>Yes, remove</button>
              <button type="button" className="ll-clr" onClick={() => setConfirmRemove(false)}>Keep them</button>
            </span>
          )}
          {mode === 'exclude' && (
            <span className="srce-row">
              <input className="srce-input" value={reason} maxLength={1000} placeholder="Why? (required)" aria-label="Reason for leaving out"
                onChange={(e) => setReason(e.target.value)} />
              <button type="button" className="srce-btn srce-btn--primary" disabled={!reason.trim() || busy} onClick={() => bulk('exclude')}>Leave out</button>
            </span>
          )}
        </div>
      )}

      <div ref={tableRef}>
        <TableShell className="srce-tbl srce-tbl--lines" aria-label="Event lines" aria-busy={!current}>
          {!current && !error && !loadError && <div className="ll-loading">Loading lines…</div>}
          {current && data && data.total === 0 && (
            <div className="ll-empty">
              <div className="ll-empty-t">No lines here</div>
              {editable ? 'Add parts by filters, paste a list, or upload a file.' : 'Nothing matches these filters.'}
            </div>
          )}
          {current && data && data.total > 0 && (
            <>
              <div className="srce-tr srce-th">
                <div>{rowEditable && <input type="checkbox" checked={allOnPage} onChange={toggleAll} aria-label="Select all on this page" />}</div>
                <div>Part</div><div>Site</div><div className="srce-r">Estimated annual volume</div>
                <div className="srce-r">Last paid</div><div className="srce-r">Projected cost</div>
                <div>Bought mostly from</div><div />
              </div>
              {items.map((l) => (
                <React.Fragment key={l.line_uid}>
                  <div className={`srce-tr${l.is_included ? '' : ' srce-tr--out'}`}>
                    <div>{rowEditable && <input type="checkbox" checked={selected.has(l.line_uid)} onChange={() => toggle(l.line_uid)} aria-label={`Select ${l.material_number}`} />}</div>
                    <div>
                      <div className="srce-mono">{l.material_number}</div>
                      <div className="srce-sub" title={l.material_name || ''}>{l.material_name || l.short_description || ''}</div>
                      {l.incomplete_reasons.length > 0 && l.is_included && (
                        <div className="srce-warn">{l.incomplete_reasons.map((c) => INCOMPLETE_TEXT[c] || c).join(' ')}</div>
                      )}
                    </div>
                    <div className="srce-sub">{l.site}</div>
                    <div className="srce-r">
                      <QuantityCell line={l} editable={rowEditable}
                        onSave={(q) => act('POST', '/lines/quantity', { items: [{ line_uid: l.line_uid, quantity: q }] })} />
                    </div>
                    <div className="srce-r">
                      <div className="srce-num">{formatPrice(l.baseline_price)}</div>
                      <div className="srce-sub">{l.baseline_date ? formatDate(l.baseline_date) : ''}</div>
                    </div>
                    <div className="srce-r srce-num">{l.projected_cost === null ? 'Not available' : formatMoney(l.projected_cost)}</div>
                    <div className="srce-sub" title={l.incumbent_supplier_name || ''}>
                      {l.incumbent_source === 'buyer' ? l.incumbent_record_name : (l.incumbent_supplier_name || 'None')}
                    </div>
                    <div>
                      <button type="button" className="srce-chev" aria-label={`Details for ${l.material_number}`}
                        aria-expanded={expanded === l.line_uid}
                        onClick={() => setExpanded(expanded === l.line_uid ? null : l.line_uid)}>
                        <svg viewBox="0 0 12 12" width="12" height="12" style={{ transform: expanded === l.line_uid ? 'rotate(90deg)' : 'none' }}><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
                      </button>
                    </div>
                  </div>
                  {expanded === l.line_uid && <LineDetail line={l} editable={rowEditable && !amending} api={api} onAct={act} />}
                </React.Fragment>
              ))}
            </>
          )}
        </TableShell>
      </div>
      {data && current && (
        <Pager data={data} onPage={setPage} onPageSize={setPageSize} pageSizeOptions={PAGE_SIZES}
          label="lines" numbered scrollTargetRef={tableRef} />
      )}
      {data && data.total > 0 && current && (
        <div className="ll-foot">
          These lines: <b>{formatMoney(data.filtered_totals.spend)}</b> spent on the parts in the event over the event window;
          {' '}projected cost at the last price paid <b>{formatMoney(data.filtered_totals.projected_cost)}</b>.
          {' '}Estimated annual volume starts as the units bought in the last 12 months, returns subtracted; change it when you expect more or less.
        </div>
      )}
    </div>
  );
}
